import { useQuery } from '@tanstack/react-query';
import type { FC } from 'react';

import { OrderApi, ReceiptApi } from '../api';
import { useAuthContext } from '../hooks';
import { Receipt } from '../model';
import {
  formatDurationRu,
  formatMedicinePlural,
  getOrderStatusConfig,
} from '../utils';

export const ClientMedicinePage = () => {
  const { isAuthorized } = useAuthContext();
  const { data: receipts } = useQuery({
    queryKey: [ReceiptApi.KEY, 'get'],
    queryFn: () => ReceiptApi.get(),
    enabled: isAuthorized,
  });

  return (
    <>
      {receipts?.map((receipt, index) => (
        <ReceiptView
          key={receipt.order_id ?? `receipt-${index}`}
          receipt={receipt}
        />
      ))}
    </>
  );
};

const ReceiptView: FC<{ receipt: Receipt }> = ({ receipt }) => {
  const { data: order } = useQuery({
    queryKey: [OrderApi.KEY, 'getById', receipt.order_id],
    queryFn: () => OrderApi.getById(receipt.order_id as number, true),
    enabled: receipt.order_id !== null && receipt.order_id !== undefined,
  });

  return (
    <div className="mb-[20px] rounded-[16px] bg-brand-1 text-white">
      <header className="flex justify-between px-[24px] py-[16px]">
        <div className="flex gap-[12px]">
          <span>Заказ #{receipt.order_id}</span>
          {order && (
            <>
              <span className="text-white/20">|</span>
              <span className="flex items-center gap-2">
                Статус{' '}
                {(() => {
                  const cfg = getOrderStatusConfig(order.status?.value ?? '');
                  return (
                    <span
                      className={`inline-flex items-center rounded-[100px] px-[12px] py-[4px] text-[14px] font-medium ${cfg.bg} ${cfg.text}`}
                    >
                      {cfg.label}
                    </span>
                  );
                })()}
              </span>
            </>
          )}
        </div>
        {receipt.doctor && (
          <span>
            Врач <span className="text-brand-5">{receipt.doctor.fullName}</span>
          </span>
        )}
      </header>
      <div className="rounded-[16px] bg-white py-[12px] text-brand-1">
        <table className="table">
          <thead>
            <tr>
              <th className="w-[24px] border-none">&nbsp;</th>
              <th className="text-left">Лекарство</th>
              <th className="text-left">Количество</th>
              <th className="text-left">Период</th>
              <th className="text-left">Время</th>
              <th className="w-[24px] border-none">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {receipt.receipt_medicine.map(
              ({ daytime, duration, eating, frequency, medicine }, index) => (
                <tr className="hoverable" key={index}>
                  <td className="border-none" />
                  <td className="font-bold">{medicine.name}</td>
                  <td>
                    {eating.value}&nbsp;
                    {formatMedicinePlural(Number(eating.value))}&nbsp;
                    {frequency.value}
                  </td>
                  <td>
                    на протяжении&nbsp;
                    {formatDurationRu({
                      days: duration,
                    })}
                  </td>
                  <td>{daytime ? daytime.value : ''}</td>
                  <td className="border-none" />
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
